import React, { createContext, useContext, useState, useEffect } from 'react';
import settingsService from '../services/settingsService';

interface AppSettings {
  nom: string;
  version: string;
  description: string;
  email: string;
  telephone: string;
  adresse: string;
  logo: string;
  cachet?: string;
}

interface SettingsContextType {
  settings: AppSettings;
  updateSettings: (newSettings: AppSettings) => void;
  refreshSettings: () => Promise<void>;
}

const defaultSettings: AppSettings = {
  nom: 'CRM Immigration',
  version: '1.0.0',
  description: 'Système de gestion d\'immigration professionnel',
  email: '',
  telephone: '',
  adresse: '',
  logo: '',
  cachet: ''
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);

  const refreshSettings = async () => {
    console.log('🔄 Chargement des paramètres depuis la base de données...');
    try {
      const response = await settingsService.getSettings();
      const loadedSettings = response.settings || response; // Gérer les deux formats de réponse
      if (loadedSettings && Object.keys(loadedSettings).length > 0) {
        console.log('✅ Paramètres chargés:', loadedSettings);
        setSettings(loadedSettings);
        settingsService.saveSettingsLocally(loadedSettings);
        try { document.title = loadedSettings.nom || defaultSettings.nom; } catch (_) {}
      } else {
        console.log('⚠️ Utilisation des paramètres par défaut');
        setSettings(defaultSettings);
        try { document.title = defaultSettings.nom; } catch (_) {}
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des paramètres:', error);
      setSettings(defaultSettings);
      try { document.title = defaultSettings.nom; } catch (_) {}
    }
  };

  const updateSettings = (newSettings: AppSettings) => {
    setSettings(newSettings);
    // Sauvegarder aussi localement
    settingsService.saveSettingsLocally(newSettings);
  };

  useEffect(() => {
    refreshSettings();

    // 🔴 NOUVEAU : Écouter les changements dans localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith('force-sync-')) {
        console.log('🔄 Synchronisation forcée détectée');
        const newSettings = JSON.parse(e.newValue || '{}');
        setSettings(newSettings);
        settingsService.saveSettingsLocally(newSettings);
      }
      if (e.key === 'app-settings') {
        console.log('🔄 Paramètres mis à jour dans localStorage');
        refreshSettings();
      }
    };

    // 🔴 NOUVEAU : Rafraîchir quand l'utilisateur revient sur l'onglet
    const handleFocus = () => {
      refreshSettings();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('tenant-settings-refresh', refreshSettings);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('tenant-settings-refresh', refreshSettings);
    };
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
export default SettingsContext;

